/**
 * client/src/features/income/components/BucketList/BucketList.jsx
 * 
 * Main bucket list component for organizing income sources.
 * Handles bucket rendering, drag-and-drop reordering, and bucket management.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import BucketItem from './BucketItem';
import AddBucketButton from './AddBucketButton';
import { calculateBucketIncome, isSameObjectId, normalizeObjectId, debugIncomeObjects } from '../../utils/incomeUtils';
import { PlusIcon } from '@heroicons/react/24/outline';
import useBuckets from '../../hooks/useBuckets';

/**
 * Toast Notification Component
 */
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const getColor = () => {
    switch (type) {
      case 'success': return 'bg-green-50 text-green-800 border-green-200';
      case 'error': return 'bg-red-50 text-red-800 border-red-200';
      case 'info': return 'bg-blue-50 text-blue-800 border-blue-200';
      default: return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };
  
  return (
    <div className={`fixed bottom-4 right-4 max-w-sm w-full p-3 rounded-lg shadow-lg border ${getColor()} animate-fade-in`}>
      <div className="flex items-start justify-between">
        <p>{message}</p>
        <button 
          onClick={onClose}
          className="ml-4 text-gray-500 hover:text-gray-700"
        >
          &times;
        </button>
      </div>
    </div>
  );
};

/**
 * BucketList Component
 * 
 * Main container for displaying and managing money buckets.
 * Supports drag-and-drop reordering, bucket creation, and income assignment.
 * 
 * @param {Object} props
 * @param {Array} props.buckets - Array of bucket objects from parent component
 * @param {Array} props.incomes - Array of income objects
 * @param {Function} props.onReorderBuckets - Function to handle bucket reordering
 * @param {Function} props.refreshIncomes - Function to refresh income data
 * @param {Boolean} props.isPartnerMode - Whether viewing partner's data
 */
const BucketList = ({ 
  buckets: propBuckets = [],
  incomes = [], 
  onReorderBuckets,
  refreshIncomes,
  isPartnerMode = false
}) => {
  // Use buckets from props instead of fetching again
  // This prevents duplicate API calls and ensures consistent data
  const buckets = propBuckets;
  
  // Get bucket management functions from hook (but don't fetch buckets again)
  const { 
    loading, 
    error, 
    reorderBuckets,
    refreshBuckets,
    createBucket,
    deleteBucket
  } = useBuckets({ isPartnerMode });
  
  // State for toast notifications
  const [toast, setToast] = useState(null);
  
  // Log bucket count whenever buckets change
  useEffect(() => {
    console.log('🧮 Current bucket count:', buckets?.length || 0);
    if (buckets?.length > 0) {
      console.log('📋 Bucket names:', buckets.map(b => b.name).join(', '));
    }
  }, [buckets]);
  
  // Debug income-bucket relationships when component mounts
  useEffect(() => {
    if (incomes && incomes.length && buckets && buckets.length) {
      debugIncomeObjects(incomes, buckets);
    }
  }, [incomes, buckets]);
  
  // Setup sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Handle drag end event
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }
    
    const oldIndex = buckets.findIndex(b => b._id === active.id);
    const newIndex = buckets.findIndex(b => b._id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    
    // Update local state
    const reorderedBuckets = arrayMove(buckets, oldIndex, newIndex);
    
    try {
      // Format the reorder data
      const reorderData = reorderedBuckets.map((bucket, index) => ({
        id: bucket._id,
        order: index
      }));
      
      // Use callback if provided
      if (onReorderBuckets) {
        await onReorderBuckets(reorderData);
      } else {
        // Otherwise use the hook
        await reorderBuckets(reorderData);
      }
      
      setToast({
        message: 'Buckets reordered successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('❌ Error reordering buckets:', error);
      setToast({
        message: 'Failed to reorder buckets',
        type: 'error'
      });
    }
  };
  
  // Clear toast notification
  const clearToast = () => setToast(null);
  
  // Handle bucket deletion notification
  const handleBucketDeleted = async (bucketId, bucketName) => {
    try {
      console.log(`🗑️ Deleting bucket: ${bucketName} (${bucketId})`);
      
      // Call the delete function from useBuckets
      await deleteBucket(bucketId);
      
      // Show success toast
      setToast({
        message: `Bucket "${bucketName}" deleted successfully`,
        type: 'success'
      });
      
      console.log('✅ Bucket deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting bucket:', error);
      setToast({
        message: `Failed to delete bucket: ${error.message}`,
        type: 'error'
      });
    }
  };
  
  // Calculate monthly total for a bucket
  const calculateMonthlyTotal = (bucketId) => {
    return calculateBucketIncome(incomes, bucketId);
  };
  
  // Handle successful bucket creation
  const handleBucketCreated = async (newBucket) => {
    console.log('✅ New bucket created:', newBucket);
    try {
      // Refresh buckets to get the latest data
      await refreshBuckets();
      
      // Show success toast
      setToast({
        message: `Bucket "${newBucket.name}" created successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('❌ Error refreshing buckets:', error);
      setToast({
        message: 'Failed to refresh buckets',
        type: 'error'
      });
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500">
        <div className="animate-pulse">Loading buckets...</div>
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        Error loading buckets: {error.message}
      </div>
    );
  }
  
  // Debug log for bucket state
  console.log('🎯 BucketList render state:', {
    hasBuckets: Boolean(buckets?.length),
    bucketsLength: buckets?.length,
    bucketsList: buckets?.map(b => ({ id: b._id, name: b.name }))
  });
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 mb-2 sm:mb-0">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Income Buckets</h2>
        <AddBucketButton onSuccess={handleBucketCreated} />
      </div>
      
      {!buckets || buckets.length === 0 ? (
        <div className="p-4 sm:p-8 text-center border border-dashed border-gray-200 rounded-lg bg-gray-50">
          <p className="text-sm sm:text-base text-gray-500 mb-3 sm:mb-4">You don't have any income buckets yet</p>
          <AddBucketButton label="Create Your First Bucket" onSuccess={handleBucketCreated} />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={buckets.map(b => b._id)} 
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3 sm:space-y-4">
              {buckets.map(bucket => {
                console.log('🎯 Rendering bucket:', bucket);
                return (
                  <BucketItem 
                    key={bucket._id}
                    bucket={bucket}
                    incomes={incomes}
                    monthlyTotal={calculateMonthlyTotal(bucket._id)}
                    refreshIncomes={refreshIncomes}
                    onBucketDeleted={handleBucketDeleted}
                    totalBuckets={buckets.length}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}
      
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={clearToast} 
        />
      )}
      
      {/* Debug button for viewing income data */}
      <button
        onClick={() => debugIncomeObjects(incomes, buckets)}
        className="text-xs text-indigo-600 hover:text-indigo-800 mt-4"
      >
        Debug: Income Data
      </button>
    </div>
  );
};

export default BucketList; 